import { get, uniqBy } from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { momentTimezone } from 'utils/common';
import { getMinutesAsMilliseconds } from 'utils/numbers';

import { PAYMENT_SCHEDULES_API } from '../../api/paymentSchedule';
import useUserGroupsQuery from '../../hooks/queries/useUserGroupsQuery';
import {
  hasCompanyAdminRole,
  hasCompanyManagerOrAdminRole,
  hasCompanyManagerRole,
  hasUserRole,
} from '../../utils/roles';
import { selectStoreCompanySettings, selectStoreCurrentCompany } from '../../utils/storeSelectors';

const CALENDAR_QUERY_STALE_TIME = getMinutesAsMilliseconds(3);
const CALENDAR_QUERY_CACHE_TIME = getMinutesAsMilliseconds(10);

const PERIOD_DATE_PARSE_FORMATS = ['MM/DD/YYYY H:mm:ss', 'MM/DD/YYYY HH:mm:ss', moment.ISO_8601];

const DEADLINE_FIELDS = Object.freeze([
  {
    key: 'paymentDate',
    color: 'red',
    badgeStatus: 'error',
    getLabel: t => t('paymentPeriodEndDate'),
  },
  {
    key: 'dateOfTripSubmissionDeadline',
    color: 'blue',
    badgeStatus: 'processing',
    getLabel: t => t('tripSubmissionDay'),
  },
  {
    key: 'dateOfManagerApprovalDeadline',
    color: 'yellow',
    badgeStatus: 'warning',
    getLabel: t => t('tripApprovalDay'),
  },
  {
    key: 'achPaymentDate',
    color: 'green',
    badgeStatus: 'success',
    getLabel: t => t('achPaymentDate'),
  },
]);

const parseDeadlineDate = rawDate => {
  if (!rawDate) return null;

  const parsedDate = moment(rawDate, [
    'MM/DD/YYYY H:mm:ss',
    'MM/DD/YYYY HH:mm:ss',
    moment.ISO_8601,
  ]);
  if (!parsedDate.isValid()) return null;

  return parsedDate.format('MM/DD/YYYY');
};

const getScheduleIdFromReference = scheduleRef => {
  if (!scheduleRef) return null;
  if (typeof scheduleRef === 'string') return scheduleRef;
  return scheduleRef?._id || null;
};

const getManagerIdFromReference = managerRef => {
  if (!managerRef) return null;
  if (typeof managerRef === 'string') return managerRef;
  return managerRef?._id || null;
};

const isManagerOfGroup = (group, managerId) => {
  if (!group || !managerId) return false;

  const isPrimaryManager = getManagerIdFromReference(group.groupManager) === managerId;
  const isAdditionalManager = Array.isArray(group.additionalManagers)
    ? group.additionalManagers.some(manager => getManagerIdFromReference(manager) === managerId)
    : false;

  return isPrimaryManager || isAdditionalManager;
};

const toScheduleObjectList = (scheduleIds, schedulesLookup, t) => {
  return uniqBy(
    scheduleIds
      .map(id => (id ? schedulesLookup[id] || { _id: id, name: t('paymentSchedule') } : null))
      .filter(Boolean),
    '_id',
  ).sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
};

const useDashboardPaymentScheduleCalendar = ({ t, user, selectedGroupId }) => {
  const currentCompany = useSelector(selectStoreCurrentCompany);
  const companySettings = useSelector(selectStoreCompanySettings);
  const groupList = useSelector(store => store.group?.groupList || []);
  const [selectedScheduleId, setSelectedScheduleId] = useState();
  const userId = user?.profile?._id;

  const userGroupsQuery = useUserGroupsQuery(userId);

  const adminPaymentSchedulesQuery = useQuery({
    enabled: hasCompanyAdminRole(user) && !!currentCompany?._id && userGroupsQuery.isFetched,
    staleTime: CALENDAR_QUERY_STALE_TIME,
    cacheTime: CALENDAR_QUERY_CACHE_TIME,
    queryKey: ['fetchDashboardCalendarAdminPaymentSchedules', currentCompany?._id],
    queryFn: () =>
      PAYMENT_SCHEDULES_API.fetchCompanyPaymentSchedules(currentCompany._id, { getUsers: false }),
  });

  const selectedGroup = useMemo(() => {
    return groupList.find(group => group?._id === selectedGroupId);
  }, [groupList, selectedGroupId]);

  const schedulesLookup = useMemo(() => {
    const scheduleObjects = [];

    if (Array.isArray(adminPaymentSchedulesQuery.data)) {
      scheduleObjects.push(...adminPaymentSchedulesQuery.data);
    }

    groupList.forEach(group => {
      if (group?.paymentScheduleId && typeof group.paymentScheduleId === 'object') {
        scheduleObjects.push(group.paymentScheduleId);
      }
    });

    if (
      user?.profile?.group?.paymentScheduleId &&
      typeof user.profile.group.paymentScheduleId === 'object'
    ) {
      scheduleObjects.push(user.profile.group.paymentScheduleId);
    }

    return uniqBy(scheduleObjects, '_id').reduce((acc, schedule) => {
      acc[schedule._id] = schedule;
      return acc;
    }, {});
  }, [adminPaymentSchedulesQuery.data, groupList, user]);

  const managerGroups = useMemo(() => {
    if (!hasCompanyManagerRole(user)) return [];
    return groupList.filter(group => isManagerOfGroup(group, userId));
  }, [groupList, user, userId]);

  const paymentSchedules = useMemo(() => {
    if (hasUserRole(user)) {
      const userScheduleId = getScheduleIdFromReference(user?.profile?.group?.paymentScheduleId);
      return userScheduleId ? toScheduleObjectList([userScheduleId], schedulesLookup, t) : [];
    }

    if (hasCompanyAdminRole(user)) {
      if (Array.isArray(adminPaymentSchedulesQuery.data)) {
        return [...adminPaymentSchedulesQuery.data].sort((a, b) =>
          (a?.name || '').localeCompare(b?.name || ''),
        );
      }

      return [];
    }

    if (hasCompanyManagerRole(user)) {
      const managerScheduleIds = managerGroups
        .map(group => getScheduleIdFromReference(group?.paymentScheduleId))
        .filter(Boolean);
      return toScheduleObjectList(managerScheduleIds, schedulesLookup, t);
    }

    return [];
  }, [user, adminPaymentSchedulesQuery.data, managerGroups, t, schedulesLookup]);

  const selectedSchedule = useMemo(() => {
    return paymentSchedules.find(schedule => schedule._id === selectedScheduleId);
  }, [paymentSchedules, selectedScheduleId]);

  const paymentScheduleOptions = useMemo(() => {
    return paymentSchedules.map(schedule => ({
      label: schedule.name,
      value: schedule._id,
    }));
  }, [paymentSchedules]);

  // Users are always pinned to their own schedule.
  useEffect(() => {
    if (!hasUserRole(user)) return;
    const userScheduleId = getScheduleIdFromReference(user?.profile?.group?.paymentScheduleId);
    setSelectedScheduleId(userScheduleId || undefined);
  }, [user]);

  // Admin/Manager: when selected header group changes, sync schedule to group schedule.
  useEffect(() => {
    if (!hasCompanyManagerOrAdminRole(user) || !selectedGroupId) return;
    const groupScheduleId = getScheduleIdFromReference(selectedGroup?.paymentScheduleId);
    if (groupScheduleId) {
      setSelectedScheduleId(groupScheduleId);
    }
  }, [selectedGroupId, selectedGroup, user]);

  useEffect(() => {
    if (!paymentSchedules.length) {
      setSelectedScheduleId(undefined);
      return;
    }

    if (
      !selectedScheduleId ||
      !paymentSchedules.find(schedule => schedule._id === selectedScheduleId)
    ) {
      setSelectedScheduleId(paymentSchedules[0]._id);
    }
  }, [paymentSchedules, selectedScheduleId]);

  const periodsQuery = useQuery({
    enabled: !!selectedScheduleId,
    staleTime: CALENDAR_QUERY_STALE_TIME,
    cacheTime: CALENDAR_QUERY_CACHE_TIME,
    placeholderData: { documents: [] },
    queryKey: ['fetchDashboardPaymentSchedulePeriods', selectedScheduleId],
    queryFn: () =>
      PAYMENT_SCHEDULES_API.fetchPaymentPeriodsByScheduleId(
        selectedScheduleId,
        undefined,
        undefined,
        true,
      ),
  });

  const calendarDates = useMemo(() => {
    const groupedDates = {};
    const seenDateFields = new Set();
    const rawDocuments = get(periodsQuery.data, 'documents', []);
    const documents = uniqBy(rawDocuments, period => parseDeadlineDate(period.paymentDate));

    documents.forEach(period => {
      DEADLINE_FIELDS.forEach(field => {
        const normalizedDate = parseDeadlineDate(period[field.key]);
        if (!normalizedDate) return;

        const dateFieldKey = `${normalizedDate}|${field.key}`;
        if (seenDateFields.has(dateFieldKey)) return;
        seenDateFields.add(dateFieldKey);

        if (!groupedDates[normalizedDate]) {
          groupedDates[normalizedDate] = {
            color: field.color,
            badgeStatus: field.badgeStatus,
            descriptions: [],
          };
        }

        groupedDates[normalizedDate].descriptions.push(field.getLabel(t));
      });
    });

    return Object.keys(groupedDates).map(date => ({
      date: `${date} 23:59:59`,
      color: groupedDates[date].color,
      badgeStatus: groupedDates[date].badgeStatus,
      description: groupedDates[date].descriptions.map((label, index) => (
        <span key={[date, label, index].join('-')}>{label}</span>
      )),
    }));
  }, [periodsQuery.data, t]);

  const deadlineGroups = useMemo(() => {
    const grouped = {};
    calendarDates.forEach((calendarDate, dateIndex) => {
      const datePart = calendarDate.date.split(' ')[0];
      const parsedDate = moment(datePart, 'MM/DD/YYYY');
      if (!parsedDate.isValid()) return;

      const monthKey = parsedDate.format('YYYY-MM');
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthKey,
          monthLabel: parsedDate.format('MMMM YYYY'),
          items: [],
        };
      }

      const descriptionLabels = Array.isArray(calendarDate.description)
        ? calendarDate.description
            .map(item => (typeof item?.props?.children === 'string' ? item.props.children : null))
            .filter(Boolean)
        : [];

      descriptionLabels.forEach((label, labelIndex) => {
        grouped[monthKey].items.push({
          id: [monthKey, dateIndex, labelIndex, label].join('-'),
          badgeStatus: calendarDate.badgeStatus,
          label,
          date: parsedDate.format('MMM D, YYYY'),
          sortTs: parsedDate.valueOf(),
        });
      });
    });

    return Object.values(grouped)
      .sort((a, b) => (a.monthKey < b.monthKey ? -1 : 1))
      .map(group => ({
        ...group,
        items: group.items.sort((a, b) => a.sortTs - b.sortTs),
      }));
  }, [calendarDates]);

  const nextDeadline = useMemo(() => {
    const now = moment().startOf('day');
    let nextItem = null;

    deadlineGroups.forEach(group => {
      group.items.forEach(item => {
        const date = momentTimezone(item.date, 'MMM D, YYYY');
        if (!date.isValid()) return;
        if (date.isBefore(now, 'day')) return;

        const candidate = {
          ...item,
          status: date.isSame(now, 'day') ? 'today' : 'upcoming',
          daysUntil: date.diff(now, 'days'),
          dateLabel: item.date,
          sortTs: date.valueOf(),
        };

        if (!nextItem || candidate.sortTs < nextItem.sortTs) {
          nextItem = candidate;
        }
      });
    });

    return nextItem;
  }, [deadlineGroups]);

  const currentPeriod = useMemo(() => {
    const documents = get(periodsQuery.data, 'documents', []);
    const now = moment();

    const upcoming = documents
      .map(period => {
        const endDate = moment(period.paymentDate, PERIOD_DATE_PARSE_FORMATS);
        return { period, endDate };
      })
      .filter(({ endDate }) => endDate.isValid() && endDate.isSameOrAfter(now, 'day'))
      .sort((a, b) => a.endDate.valueOf() - b.endDate.valueOf());

    return upcoming[0]?.period || null;
  }, [periodsQuery.data]);

  const nextPeriod = useMemo(() => {
    const documents = get(periodsQuery.data, 'documents', []);
    const now = moment();

    const upcoming = documents
      .map(period => {
        const endDate = moment(period.paymentDate, PERIOD_DATE_PARSE_FORMATS);
        return { period, endDate };
      })
      .filter(({ endDate }) => endDate.isValid() && endDate.isSameOrAfter(now, 'day'))
      .sort((a, b) => a.endDate.valueOf() - b.endDate.valueOf());

    return upcoming[1]?.period || null;
  }, [periodsQuery.data]);

  const achFundingWarning = useMemo(() => {
    if (!companySettings?.ach_enabled) return null;

    const documents = get(periodsQuery.data, 'documents', []);
    const now = moment();

    let nearestDate = null;
    documents.forEach(period => {
      const rawDate = period?.achFundingDeadlineDate || period?.dateOfAchTransfer;
      if (!rawDate) return;

      const parsedDate = moment(rawDate, [
        'MM/DD/YYYY H:mm:ss',
        'MM/DD/YYYY HH:mm:ss',
        moment.ISO_8601,
      ]);
      if (!parsedDate.isValid()) return;
      if (parsedDate.isBefore(now, 'day')) return;

      if (!nearestDate || parsedDate.isBefore(nearestDate)) {
        nearestDate = parsedDate;
      }
    });

    if (!nearestDate) return null;

    const daysUntil = nearestDate.startOf('day').diff(now.clone().startOf('day'), 'days');
    if (daysUntil > 7) return null;

    return {
      dateLabel: nearestDate.format('MMM D, YYYY'),
      daysUntil,
    };
  }, [companySettings?.ach_enabled, periodsQuery.data]);

  const activeMonthDate = useMemo(() => new Date(), [selectedSchedule]);
  const isAdminSchedulesInitialLoading =
    hasCompanyAdminRole(user) && adminPaymentSchedulesQuery.isLoading;
  const isScheduleBootstrapLoading =
    !!selectedGroupId &&
    !selectedScheduleId &&
    (hasUserRole(user) || hasCompanyManagerOrAdminRole(user));
  const isPeriodsInitialLoading = !!selectedScheduleId && periodsQuery.isLoading;

  return {
    isLoading:
      isAdminSchedulesInitialLoading || isScheduleBootstrapLoading || isPeriodsInitialLoading,
    paymentSchedules,
    paymentScheduleOptions,
    selectedScheduleId,
    activeMonthDate,
    calendarDates,
    deadlineGroups,
    nextDeadline,
    currentPeriod,
    nextPeriod,
    achFundingWarning,
    setSelectedScheduleId,
  };
};

export default useDashboardPaymentScheduleCalendar;
